import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, ArrowLeft, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { BRAND } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { api, setToken, setRole } from '../lib/api';

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast({ title: 'Missing fields', description: 'Please enter email and password.' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/login', { email: form.email, password: form.password });
      setToken(data.token); setRole('admin');
      toast({ title: `Welcome, ${data.admin.name}`, description: 'Redirecting to dashboard...' });
      navigate('/admin');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed';
      toast({ title: 'Login failed', description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen relative overflow-hidden flex items-center py-12">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f7fbf3] via-[#fdfaf1] to-[#f4f9ef]" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#F0B93C]/15 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#0B5E2A]/15 blur-3xl" />
      </div>

      <div className="max-w-md mx-auto container-px w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#4b5e4d] hover:text-[#0B5E2A] mb-5 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to portal
        </Link>

        <div className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-8 shadow-[0_20px_50px_-25px_rgba(11,94,42,0.25)]">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#06401d] flex items-center justify-center">
              <ShieldCheck className="h-7 w-7 text-[#F0B93C]" />
            </div>
            <h1 className="mt-4 font-display text-2xl text-[#0B5E2A]">Admin Login</h1>
            <p className="mt-2 text-sm text-[#4b5e4d]">{BRAND.name} · Staff access only</p>
          </div>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                <input name="email" type="email" value={form.email} onChange={onChange} placeholder="you@hcc.ca" className="w-full pl-10 pr-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-[#2e4030]">Password</label>
                <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link" className="text-xs font-semibold text-[#0B5E2A] hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                <input name="password" type={showPw ? 'text' : 'password'} value={form.password} onChange={onChange} placeholder="••••••••" data-testid="admin-login-password" className="w-full pl-10 pr-10 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="admin-login-toggle-password" aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7e6d] hover:text-[#0B5E2A]">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-green w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md disabled:opacity-60">
              {busy ? 'Signing in...' : (<>Sign In <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default Login;
